import { useEffect, useRef, useCallback } from 'react';
import { Button } from './ui';
import { playClick } from '../sounds';
import './LevelCompletePopup.css';

interface LevelCompletePopupProps {
  levelName: string;
  solution: boolean[][];
  onClose: () => void;
}

function FullSolutionCanvas({ solution }: { solution: boolean[][] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rows = solution.length;
  const cols = solution[0]?.length ?? rows;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0) return;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cw = w / cols;
    const ch = h / rows;
    const cornerR = Math.min(cw, ch) * 0.12;

    ctx.clearRect(0, 0, w, h);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (!solution[r][c]) continue;
        const x = c * cw;
        const y = r * ch;
        const tl = (r === 0 && c === 0) ? cornerR : 0;
        const tr = (r === 0 && c === cols - 1) ? cornerR : 0;
        const bl = (r === rows - 1 && c === 0) ? cornerR : 0;
        const br = (r === rows - 1 && c === cols - 1) ? cornerR : 0;

        ctx.beginPath();
        ctx.moveTo(x + tl, y);
        ctx.lineTo(x + cw - tr, y);
        if (tr) ctx.quadraticCurveTo(x + cw, y, x + cw, y + tr); else ctx.lineTo(x + cw, y);
        ctx.lineTo(x + cw, y + ch - br);
        if (br) ctx.quadraticCurveTo(x + cw, y + ch, x + cw - br, y + ch); else ctx.lineTo(x + cw, y + ch);
        ctx.lineTo(x + bl, y + ch);
        if (bl) ctx.quadraticCurveTo(x, y + ch, x, y + ch - bl); else ctx.lineTo(x, y + ch);
        ctx.lineTo(x, y + tl);
        if (tl) ctx.quadraticCurveTo(x, y, x + tl, y); else ctx.lineTo(x, y);
        ctx.closePath();
        ctx.fillStyle = '#4a2008';
        ctx.fill();
      }
    }
  }, [solution, rows, cols]);

  return (
    <canvas
      ref={canvasRef}
      className="lcp-canvas"
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
}

const CONFETTI_COUNT = 40;

function Confetti() {
  const colors = ['#f0b830', '#ffe080', '#ffda68', '#e07830', '#c08010', '#4a9e4a', '#5bb8e0', '#e06080'];
  const pieces = Array.from({ length: CONFETTI_COUNT }, (_, i) => {
    const color = colors[i % colors.length];
    const left = Math.random() * 100;
    const delay = Math.random() * 1.2;
    const duration = 2 + Math.random() * 1.5;
    const size = 6 + Math.random() * 6;
    const rotation = Math.random() * 360;
    const drift = -30 + Math.random() * 60;
    return { color, left, delay, duration, size, rotation, drift };
  });

  return (
    <div className="lcp-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="lcp-confetti-piece"
          style={{
            '--left': `${p.left}%`,
            '--delay': `${p.delay}s`,
            '--duration': `${p.duration}s`,
            '--size': `${p.size}px`,
            '--rotation': `${p.rotation}deg`,
            '--drift': `${p.drift}px`,
            backgroundColor: p.color,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}

export function LevelCompletePopup({
  levelName,
  solution,
  onClose,
}: LevelCompletePopupProps) {
  const rows = solution.length;
  const cols = solution[0]?.length ?? rows;
  const cardRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const card = cardRef.current;
    if (!card) return;
    const focusable = card.querySelectorAll<HTMLElement>('button');
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, []);

  useEffect(() => {
    const btn = cardRef.current?.querySelector<HTMLElement>('button');
    btn?.focus();
  }, []);

  return (
    <div className="lcp-overlay" role="presentation">
      <Confetti />
      <div
        ref={cardRef}
        className="lcp-card"
        role="dialog"
        aria-modal="true"
        aria-label="Level Complete"
        onKeyDown={handleKeyDown}
      >
        <h2 className="lcp-title">Level Complete!</h2>

        <div
          className="lcp-solution"
          style={{ aspectRatio: `${cols} / ${rows}` }}
        >
          <FullSolutionCanvas solution={solution} />
        </div>

        <span className="lcp-name">{levelName}</span>

        <div className="lcp-buttons">
          <Button
            variant="primary"
            onClick={() => { playClick(); onClose(); }}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
